import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import AutoChart from '../components/AutoChart';
import MarkdownBlock from '../components/MarkdownBlock';
import { ScrollArea } from '../components/ScrollArea';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Send, Plus, MessageSquare, Trash2, ChevronDown, ChevronRight, ChevronLeft,
  Database, Table2, Columns, Sparkles, Download, Copy, Check, Loader2, Mic, Bookmark,
  RotateCw, FileDown, Zap, MoreHorizontal,
} from 'lucide-react';
import { toast } from 'sonner';
import { streamChatV2Post } from '../utils/sseChat';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('dqp_token')}` } });

const CHAT_HISTORY_COLLAPSED_KEY = 'dqp_chat_history_collapsed';

/** Format Mongo / ISO timestamp for session list (compact relative + fallback date). */
function formatSessionTimestamp(updatedAt, createdAt) {
  const raw = updatedAt ?? createdAt;
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (diff < 0) return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  if (mins < 1) return 'Now';
  if (mins === 1) return '1 min ago';
  if (mins < 60) return `${mins} min ago`;
  if (hrs === 1) return '1 hr ago';
  if (hrs < 24) return `${hrs} hr ago`;
  if (days === 1) return '1 day ago';
  if (days < 7) return `${days} days ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined });
}

/** Recents list shown beside collapsed chat sidebar (popover). */
function ChatRecentsPopoverPanel({
  sessionsLoading,
  sessions,
  activeSession,
  onPickSession,
  onDeleteSession,
}) {
  return (
    <>
      <div className="border-b border-[#e6e8f2] px-3 py-2.5 bg-[#fafbfe] rounded-t-lg">
        <h3 className="text-sm font-semibold tracking-tight text-[#1e1e2e]">Recents</h3>
      </div>
      <ScrollArea className="max-h-[min(380px,calc(100vh-10rem))] overflow-y-auto p-2 scrollbar-light">
        {sessionsLoading ? (
          <div className="space-y-2 px-1 py-0.5" aria-busy="true">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={`rec-sk-${i}`} className="flex gap-3 rounded-lg px-2 py-2">
                <div className="h-10 flex-1 space-y-2 py-0.5">
                  <div className="h-3 w-[88%] animate-pulse rounded bg-[#e8eaef]" />
                  <div className="h-2.5 w-[36%] animate-pulse rounded bg-[#e8eaef]" />
                </div>
              </div>
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <p className="px-3 py-6 text-center text-xs text-[#9ca3af]">No conversations yet</p>
        ) : (
          <ul className="space-y-0.5">
            {sessions.map((s) => {
              const active = activeSession === s.session_id;
              const timeLabel = formatSessionTimestamp(s.updated_at, s.created_at);
              return (
                <li key={s.session_id}>
                  <div
                    className={`flex items-stretch gap-0 rounded-lg transition-colors duration-150 ${
                      active ? 'bg-[#e8eaef]' : 'hover:bg-[#f1f5f9]'
                    }`}
                  >
                    <button
                      type="button"
                      data-testid={`session-${s.session_id}`}
                      aria-current={active ? 'true' : undefined}
                      className="flex min-w-0 flex-1 flex-col items-start gap-0.5 px-3 py-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-[#5b63ea]/35 focus-visible:ring-offset-1 rounded-lg"
                      onClick={() => onPickSession(s.session_id)}
                    >
                      <span className={`w-full truncate text-[13px] leading-snug text-[#1e1e2e] ${active ? 'font-semibold' : 'font-medium'}`}>
                        {s.title || 'Untitled'}
                      </span>
                      {timeLabel ? (
                        <span className="text-[11px] leading-snug text-[#9ca3af]">{timeLabel}</span>
                      ) : null}
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          aria-label="Conversation actions"
                          className="flex shrink-0 items-center justify-center rounded-md px-2 text-[#64748b] outline-none transition-colors hover:bg-black/[0.04] hover:text-[#1e1e2e] focus-visible:ring-2 focus-visible:ring-[#5b63ea]/35 data-[state=open]:bg-black/[0.06]"
                          onClick={(e) => e.stopPropagation()}
                          onPointerDown={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal size={18} strokeWidth={2} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        side="right"
                        sideOffset={6}
                        className="z-[60] min-w-[9rem] border-[#e6e8f2] bg-white p-1 shadow-lg text-[#1e1e2e]"
                      >
                        <DropdownMenuItem
                          className="gap-2 text-sm text-red-600 focus:bg-red-50 focus:text-red-700 cursor-pointer"
                          onSelect={(e) => {
                            onDeleteSession(e, s.session_id);
                          }}
                        >
                          <Trash2 size={14} className="text-red-500" strokeWidth={2} />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </ScrollArea>
    </>
  );
}

/** Icon-only control with hover tooltip (label also use aria-label on trigger). */
function IconActionTooltip({ label, side = 'top', children }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} className="border border-slate-700 bg-[#1e293b] text-white text-xs font-medium shadow-lg">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

/** Overrides outline variant hover (accent-foreground) so Lucide currentColor stays visible. */
const ASSISTANT_FOOTER_ICON_BTN =
  'h-9 w-9 shrink-0 rounded-lg !border-0 bg-transparent text-[#5b63ea] shadow-none hover:!border-0 hover:!bg-[#f1f5f9] hover:!text-[#5b63ea] disabled:opacity-40';

function ConfidenceBadge({ confidence }) {
  const level = confidence >= 0.85 ? 'High' : confidence >= 0.6 ? 'Medium' : 'Low';
  const color = confidence >= 0.85 ? 'bg-emerald-100 text-emerald-700' : confidence >= 0.6 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
  return (
    <span data-testid="confidence-badge" className={`text-[10px] font-semibold px-1 py-0.5 rounded-full ${color}`}>
      {level} ({Math.round(confidence * 100)}%)
    </span>
  );
}

function SQLDisplay({ sql }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(sql);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!sql) return null;
  return (
    <div data-testid="sql-display" className="mt-3 border border-[#e6e8f2] rounded-lg overflow-hidden">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-3 py-2 bg-[#f8f9fc] hover:bg-[#f0f1f6] text-xs font-medium text-[#6b7280] transition-colors">
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <Database size={12} className="text-[#5b63ea]" />
        SQL
      </button>
      {open && (
        <div className="relative px-4 py-3 bg-[#1e1e2e]">
          <button type="button" onClick={handleCopy} className="absolute top-2 right-2 text-[#9ca3af] hover:text-white transition-colors">
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </button>
          <pre className="text-[#e2e8f0] text-xs overflow-x-auto whitespace-pre-wrap" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            {sql}
          </pre>
        </div>
      )}
    </div>
  );
}

function QueryPlan({ plan }) {
  const [open, setOpen] = useState(false);
  if (!plan || !plan.intent) return null;
  return (
    <div data-testid="query-plan" className="mt-2 border border-[#e6e8f2] rounded-lg overflow-hidden">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-3 py-2 bg-[#f8f9fc] hover:bg-[#f0f1f6] text-xs font-medium text-[#6b7280] transition-colors">
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <Sparkles size={12} className="text-[#8b5cf6]" />
        Query Plan
      </button>
      {open && (
        <div className="px-4 py-3 space-y-2 text-xs">
          <div className="flex gap-4 flex-wrap">
            <span className="text-[#6b7280]">Intent: <strong className="text-[#1e1e2e]">{plan.intent}</strong></span>
            {plan.aggregation && plan.aggregation !== 'NONE' && (
              <span className="text-[#6b7280]">Aggregation: <strong className="text-[#1e1e2e]">{plan.aggregation}</strong></span>
            )}
          </div>
          {plan.tables_used?.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <Table2 size={11} className="text-[#5b63ea]" />
              {plan.tables_used.map((t, i) => (
                <span key={i} className="bg-[#5b63ea]/10 text-[#5b63ea] px-1.5 py-0.5 rounded text-[10px] font-medium">{t}</span>
              ))}
            </div>
          )}
          {plan.columns_used?.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <Columns size={11} className="text-[#10b981]" />
              {plan.columns_used.map((c, i) => (
                <span key={i} className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded text-[10px] font-medium">{c}</span>
              ))}
            </div>
          )}
          {plan.filters?.length > 0 && (
            <div className="text-[#6b7280]">Filters: {plan.filters.join(', ')}</div>
          )}
        </div>
      )}
    </div>
  );
}

function DataLineage({ lineage }) {
  if (!lineage?.length) return null;
  return (
    <div data-testid="data-lineage" className="mt-2 flex items-center gap-1.5 flex-wrap">
      <span className="text-[10px] text-[#9ca3af] font-medium uppercase tracking-wider">Lineage:</span>
      {lineage.map((item, i) => (
        <span key={i} className="text-[10px] bg-[#f5f6fb] text-[#6b7280] px-1.5 py-0.5 rounded border border-[#e6e8f2]">{item}</span>
      ))}
    </div>
  );
}

function updateLastAssistant(messages, updater) {
  const next = [...messages];
  const i = next.length - 1;
  if (i < 0 || next[i].role !== 'assistant') return messages;
  next[i] = updater(next[i]);
  return next;
}

function parseOutputResults(content) {
  if (content == null || typeof content !== 'string') return null;
  try {
    const o = JSON.parse(content);
    if (Array.isArray(o.columns) && Array.isArray(o.rows)) return o;
    return null;
  } catch {
    return null;
  }
}

function StreamingDots({ label }) {
  return (
    <div className="flex items-center gap-3 py-2 text-sm text-[#6b7280]" data-testid="streaming-indicator">
      <div className="flex gap-1">
        <span className="w-2 h-2 rounded-full bg-[#5b63ea] animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-2 h-2 rounded-full bg-[#5b63ea] animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-2 h-2 rounded-full bg-[#5b63ea] animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
      <span>{label || 'Thinking…'}</span>
    </div>
  );
}

function SectionCard({ title, icon: Icon, children, accent }) {
  return (
    <div className={`rounded-xl border px-4 py-3 mb-3 last:mb-0 ${accent || 'border-[#e6e8f2] bg-[#fafbfe]'}`}>
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon size={14} className="text-[#5b63ea]" />}
        <span className="text-[11px] font-semibold uppercase tracking-wide text-[#9ca3af]">{title}</span>
      </div>
      {children}
    </div>
  );
}

function StreamSection({ sec, defaultLogicOpen }) {
  const { type, content, metadata } = sec;
  const [logicOpen, setLogicOpen] = useState(defaultLogicOpen ?? false);

  if (type === 'error') {
    return (
      <SectionCard title="Error" accent="border-red-200 bg-red-50/80">
        <p className="text-sm text-red-800 whitespace-pre-wrap">{content}</p>
      </SectionCard>
    );
  }

  if (type === 'summary') {
    const conf = metadata?.confidence;
    return (
      <SectionCard title="Summary" icon={Zap}>
        <div className="text-sm text-[#4b5563] leading-relaxed">
          <MarkdownBlock>{content}</MarkdownBlock>
        </div>
        {typeof conf === 'number' && conf > 0 && (
          <div className="mt-2 flex items-center gap-2">
            <ConfidenceBadge confidence={conf} />
          </div>
        )}
      </SectionCard>
    );
  }

  if (type === 'answer') {
    return (
      <div className="mb-4">
        <div className="text-[15px] text-[#1e1e2e] leading-relaxed">
          <MarkdownBlock>{content}</MarkdownBlock>
        </div>
      </div>
    );
  }

  if (type === 'logic') {
    const sql = metadata?.sql_query;
    const plan = metadata?.query_plan;
    return (
      <div className="mb-3 border border-[#e6e8f2] rounded-xl overflow-hidden bg-white">
        <button
          type="button"
          onClick={() => setLogicOpen(!logicOpen)}
          className="w-full flex items-center gap-2 px-4 py-2.5 bg-[#f8f9fc] hover:bg-[#f0f1f6] text-xs font-medium text-[#6b7280] transition-colors"
        >
          {logicOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <Sparkles size={14} className="text-[#8b5cf6]" />
          How it works
        </button>
        {logicOpen && (
          <div className="px-4 py-3 border-t border-[#e6e8f2] space-y-3">
            <div className="text-sm text-[#4b5563] leading-relaxed">
              <MarkdownBlock>{content}</MarkdownBlock>
            </div>
            {sql && <SQLDisplay sql={sql} />}
            {plan && <QueryPlan plan={plan} />}
          </div>
        )}
      </div>
    );
  }

  if (type === 'output') {
    const results = parseOutputResults(content);
    if (results) {
      return (
        <div className="mb-3 last:mb-0">
          <div className="flex items-center gap-2 mb-2">
            <Table2 size={14} className="text-[#5b63ea]" />
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[#9ca3af]">Results</span>
          </div>
          <AutoChart results={results} />
        </div>
      );
    }
    let parsed;
    try {
      parsed = JSON.parse(content);
    } catch {
      parsed = null;
    }
    if (parsed?.success && parsed.affected_rows != null) {
      return (
        <SectionCard title="Results" icon={Table2}>
          <p className="text-sm text-[#4b5563]">{parsed.affected_rows} row(s) affected.</p>
        </SectionCard>
      );
    }
    return (
      <SectionCard title="Output" icon={Table2}>
        <pre className="text-xs bg-[#1e1e2e] text-[#e2e8f0] p-3 rounded-lg overflow-x-auto whitespace-pre-wrap">{content}</pre>
      </SectionCard>
    );
  }

  return (
    <SectionCard title={type || 'Note'}>
      <div className="text-sm text-[#4b5563] whitespace-pre-wrap">{typeof content === 'string' ? content : JSON.stringify(content)}</div>
    </SectionCard>
  );
}

function AssistantMessageV2({
  msg,
  messageIndex,
  messages,
  selectedSource,
  activeSession,
  isLatestAssistant,
  onRetry,
  retryDisabled,
}) {
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveQueryName, setSaveQueryName] = useState('');
  const [saveSubmitting, setSaveSubmitting] = useState(false);

  const sections = msg.sections || [];
  const streaming = msg.streaming;
  const resultsForSave = [...sections].reverse().map((s) => parseOutputResults(s.content)).find(Boolean);
  const showResultActions = !streaming && resultsForSave?.rows?.length > 0;
  const showElapsed = !streaming && msg.elapsed_ms != null;
  const showRetryAction = Boolean(isLatestAssistant && activeSession && !streaming && onRetry);

  const openSaveModal = () => {
    const answerSec = sections.find((s) => s.type === 'answer');
    const defaultName = (answerSec?.content || '').slice(0, 50).replace(/\s+/g, ' ').trim();
    setSaveQueryName(defaultName || 'Saved query');
    setSaveModalOpen(true);
  };

  const handleSaveQueryConfirm = async () => {
    const name = saveQueryName.trim();
    if (!name) {
      toast.error('Enter a name for this query');
      return;
    }
    setSaveSubmitting(true);
    try {
      await axios.post(`${API}/queries/save`, {
        name,
        query_text: messages[messageIndex - 1]?.content || '',
        datasource_id: selectedSource,
        session_id: activeSession || '',
        results_snapshot: resultsForSave,
      }, authH());
      toast.success('Query saved!');
      setSaveModalOpen(false);
    } catch {
      toast.error('Failed to save query');
    } finally {
      setSaveSubmitting(false);
    }
  };

  return (
    <>
    <div data-testid={`msg-assistant-${messageIndex}`} className="flex flex-col gap-2 max-w-[min(880px,100%)] w-full">
      <div className="flex items-center gap-2 text-[11px] text-[#9ca3af] font-medium uppercase tracking-wider px-0.5">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-lg bg-gradient-to-br from-[#5b63ea] to-[#8b5cf6]">
          <Sparkles size={12} className="text-white" />
        </span>
        DQP Assistant
      </div>

      <div
        className={`rounded-2xl px-5 py-4 shadow-sm transition-all duration-300 ${
          streaming
            ? 'bg-white border-2 border-[#5b63ea]/25 shadow-[0_8px_30px_rgba(91,99,234,0.08)]'
            : 'bg-white border border-[#e6e8f2]'
        }`}
      >
        {streaming && sections.length === 0 && (
          <StreamingDots label={msg.statusHint || 'Connecting…'} />
        )}

        {sections.map((sec, si) => (
          <StreamSection key={`${messageIndex}-${si}-${sec.type}`} sec={sec} defaultLogicOpen={false} />
        ))}

        {(showResultActions || showElapsed || showRetryAction) && (
          <div
            className="mt-3 flex flex-wrap items-center gap-3 pt-3 border-t border-[#e6e8f2]"
            data-testid="assistant-footer-bar"
          >
            <div className="flex flex-wrap items-center gap-0.5">
              {showResultActions && (
                <>
                  <IconActionTooltip label="Export results as CSV" side="top">
                    <Button
                      variant="outline"
                      size="icon"
                      type="button"
                      data-testid="export-csv-btn"
                      aria-label="Export results as CSV"
                      className={ASSISTANT_FOOTER_ICON_BTN}
                      onClick={() => {
                        const header = resultsForSave.columns.join(',');
                        const rows = resultsForSave.rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
                        const csv = [header, ...rows].join('\n');
                        const blob = new Blob([csv], { type: 'text/csv' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `query_results_${Date.now()}.csv`;
                        a.click();
                        URL.revokeObjectURL(url);
                        toast.success('CSV exported');
                      }}
                    >
                      <Download size={18} strokeWidth={2.25} />
                    </Button>
                  </IconActionTooltip>
                  <IconActionTooltip label="Save query to library" side="top">
                    <Button
                      variant="outline"
                      size="icon"
                      type="button"
                      data-testid="save-query-btn"
                      aria-label="Save query to library"
                      className={ASSISTANT_FOOTER_ICON_BTN}
                      onClick={openSaveModal}
                    >
                      <Bookmark size={18} strokeWidth={2.25} />
                    </Button>
                  </IconActionTooltip>
                </>
              )}
              {showRetryAction && (
                  <IconActionTooltip label="Retry last answer (same question)" side="top">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      aria-label="Retry answer"
                      data-testid="chat-retry-btn"
                      disabled={retryDisabled}
                      className={ASSISTANT_FOOTER_ICON_BTN}
                      onClick={onRetry}
                    >
                      <RotateCw size={18} strokeWidth={2.25} />
                    </Button>
                  </IconActionTooltip>
              )}
            </div>
            {showElapsed && (
              <span
                className="text-[11px] text-[#64748b] tabular-nums font-medium ml-auto shrink-0"
                data-testid="assistant-elapsed-footer"
              >
                Completed in {msg.elapsed_ms} ms
              </span>
            )}
          </div>
        )}
      </div>
    </div>

    <Dialog
      open={saveModalOpen}
      onOpenChange={(open) => {
        setSaveModalOpen(open);
        if (!open) setSaveSubmitting(false);
      }}
    >
      <DialogContent
        data-testid="save-query-modal"
        closeClassName="rounded-lg p-1.5 text-[#64748b] opacity-100 hover:bg-[#f1f5f9] hover:text-[#1e293b] focus:ring-[#5b63ea]/35 ring-offset-white data-[state=open]:bg-transparent data-[state=open]:text-[#64748b]"
        className="sm:max-w-md gap-4 rounded-xl border-[#e6e8f2] bg-white p-6 text-[#1e293b] shadow-xl"
      >
        <DialogHeader>
          <DialogTitle className="text-[#1e293b] font-semibold">Save query</DialogTitle>
          <DialogDescription className="text-sm text-[#64748b]">
            Give this query a name so you can find it in your saved queries library.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2 py-1">
          <label htmlFor="save-query-name" className="text-sm font-medium text-[#334155]">
            Title
          </label>
          <Input
            id="save-query-name"
            data-testid="save-query-name-input"
            value={saveQueryName}
            onChange={(e) => setSaveQueryName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (!saveSubmitting && saveQueryName.trim()) handleSaveQueryConfirm();
              }
            }}
            placeholder="e.g. Monthly revenue summary"
            className="border-[#e6e8f2] bg-[#f8f9fc] text-[#1e293b] placeholder:text-[#94a3b8] focus-visible:border-[#5b63ea] focus-visible:ring-[#5b63ea]/25"
            autoFocus
          />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            data-testid="save-query-cancel-btn"
            disabled={saveSubmitting}
            className="border-[#e6e8f2] bg-white text-[#334155] hover:!bg-[#f8f9fc] hover:!text-[#1e293b] hover:!border-[#cbd5e1]"
            onClick={() => setSaveModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="save-query-confirm-btn"
            disabled={saveSubmitting || !saveQueryName.trim()}
            className="bg-[#5b63ea] text-white hover:bg-[#4d56e0] hover:text-white disabled:opacity-50"
            onClick={handleSaveQueryConfirm}
          >
            {saveSubmitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

export default function Chat() {
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [historyCollapsed, setHistoryCollapsed] = useState(() => (
    typeof window !== 'undefined' && localStorage.getItem(CHAT_HISTORY_COLLAPSED_KEY) === '1'
  ));

  const toggleHistoryCollapsed = useCallback(() => {
    setHistoryCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(CHAT_HISTORY_COLLAPSED_KEY, next ? '1' : '0');
      } catch { /* ignore */ }
      return next;
    });
  }, []);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [recentsPopoverOpen, setRecentsPopoverOpen] = useState(false);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, scrollToBottom]);

  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/datasources`, authH());
        setSources(data);
        setSelectedSource((prev) => (prev && data.some((s) => s.id === prev) ? prev : (data[0]?.id ?? '')));
      } catch { /* ignore */ }
    };
    load();
  }, []);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const { data } = await axios.get(`${API}/chat/sessions`, authH());
      setSessions(data);
    } catch { /* ignore */ }
    finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    if (!historyCollapsed) setRecentsPopoverOpen(false);
  }, [historyCollapsed]);

  useEffect(() => {
    const datasourceId = typeof selectedSource === 'string' ? selectedSource.trim() : '';
    if (!datasourceId) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      return;
    }
    let cancelled = false;
    setSuggestionsLoading(true);
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/chat/suggestions`, {
          ...authH(),
          params: { datasource_id: datasourceId },
        });
        if (!cancelled) setSuggestions(data.suggestions || []);
      } catch {
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setSuggestionsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedSource]);

  const loadSession = async (sessionId) => {
    try {
      const { data } = await axios.get(`${API}/chat/sessions/${sessionId}`, authH());
      setActiveSession(sessionId);
      setMessages(data.messages || []);
      if (data.datasource_id) setSelectedSource(data.datasource_id);
    } catch {
      toast.error('Failed to load session');
    }
  };

  const handleNewChat = () => {
    abortRef.current?.abort();
    setActiveSession(null);
    setMessages([]);
  };

  const handleDeleteSession = async (e, sessionId) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/chat/sessions/${sessionId}`, authH());
      setSessions((s) => s.filter((x) => x.session_id !== sessionId));
      if (activeSession === sessionId) handleNewChat();
      toast.success('Session deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleExportConversation = async () => {
    if (!activeSession) {
      toast.error('Open a conversation first');
      return;
    }
    try {
      const token = localStorage.getItem('dqp_token');
      const res = await fetch(`${API}/chat/v2/session/${activeSession}/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversation_${activeSession.slice(0, 8)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Conversation exported');
    } catch {
      toast.error('Export failed');
    }
  };

  const runStream = useCallback(async ({ endpoint, body }) => {
    const token = localStorage.getItem('dqp_token');
    if (!token) {
      toast.error('Sign in required');
      setLoading(false);
      return;
    }

    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    try {
      await streamChatV2Post(
        `${API}${endpoint}`,
        body,
        token,
        (eventName, data) => {
          if (eventName === 'status') {
            setMessages((prev) => updateLastAssistant(prev, (m) => ({
              ...m,
              statusHint: data.message,
              statusStep: data.step,
            })));
          }
          if (eventName === 'section') {
            setMessages((prev) => updateLastAssistant(prev, (m) => ({
              ...m,
              sections: [...(m.sections || []), data],
            })));
          }
          if (eventName === 'complete') {
            setMessages((prev) => updateLastAssistant(prev, (m) => ({
              ...m,
              streaming: false,
              elapsed_ms: data.elapsed_ms,
            })));
            if (data.session_id) setActiveSession(data.session_id);
            loadSessions();
          }
          if (eventName === 'error') {
            const errText = data.error || 'Stream error';
            setMessages((prev) => updateLastAssistant(prev, (m) => ({
              ...m,
              streaming: false,
              sections: [...(m.sections || []), { type: 'error', content: errText, metadata: null }],
            })));
            toast.error(errText);
          }
        },
        ac.signal,
      );
    } catch (e) {
      if (e.name === 'AbortError') return;
      const msgText = e.message || String(e);
      setMessages((prev) => updateLastAssistant(prev, (m) => ({
        ...m,
        streaming: false,
        sections: [...(m.sections || []), { type: 'error', content: msgText, metadata: null }],
      })));
      toast.error('Request failed');
    } finally {
      setMessages((prev) => updateLastAssistant(prev, (m) => (m.streaming ? { ...m, streaming: false } : m)));
      setLoading(false);
      abortRef.current = null;
      inputRef.current?.focus();
    }
  }, [loadSessions]);

  const handleSend = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    if (!selectedSource) {
      toast.error('Please connect a data source first');
      return;
    }
    setInput('');

    const userMsg = { role: 'user', content: msg, timestamp: new Date().toISOString() };
    const assistantPlaceholder = {
      role: 'assistant',
      v2: true,
      streaming: true,
      sections: [],
      statusHint: 'Processing your query…',
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMsg, assistantPlaceholder]);
    setLoading(true);

    await runStream({
      endpoint: '/chat/v2/query-stream',
      body: {
        message: msg,
        session_id: activeSession || '',
        datasource_id: selectedSource,
      },
    });
  };

  const handleRetry = async () => {
    if (!activeSession || loading || !selectedSource) {
      toast.error('Need an active chat session');
      return;
    }
    setMessages((prev) => {
      const next = [...prev];
      if (next.length && next[next.length - 1].role === 'assistant') next.pop();
      return [...next, {
        role: 'assistant',
        v2: true,
        streaming: true,
        sections: [],
        statusHint: 'Retrying…',
        timestamp: new Date().toISOString(),
      }];
    });
    setLoading(true);
    await runStream({
      endpoint: '/chat/v2/query-retry',
      body: {
        session_id: activeSession,
        datasource_id: selectedSource,
      },
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const lastAssistantStreaming = messages.length > 0 && messages[messages.length - 1].role === 'assistant' && messages[messages.length - 1].streaming;

  const lastAssistantMessageIndex = useMemo(() => {
    for (let j = messages.length - 1; j >= 0; j -= 1) {
      if (messages[j]?.role === 'assistant') return j;
    }
    return -1;
  }, [messages]);

  return (
    <DashboardLayout>
      <TooltipProvider delayDuration={200}>
      <div data-testid="chat-page" className="flex h-[calc(100vh-3.5rem)] overflow-hidden">
        <div
          data-testid="chat-sessions-panel"
          className={`border-r border-[#e6e8f2] bg-white flex flex-col flex-shrink-0 hidden md:flex transition-[width] duration-200 ease-out overflow-hidden ${
            historyCollapsed ? 'w-[52px]' : 'w-[280px]'
          }`}
        >
          {historyCollapsed ? (
            <div className="p-2 flex flex-col items-center gap-2">
              <IconActionTooltip label="Expand conversation history" side="right">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  data-testid="chat-history-expand-btn"
                  aria-label="Expand conversation history"
                  className="h-9 w-9 shrink-0 border-[#e6e8f2] text-[#5b63ea] hover:bg-[#5b63ea] hover:text-white hover:border-[#5b63ea]"
                  onClick={toggleHistoryCollapsed}
                >
                  <ChevronRight size={18} className="text-current" />
                </Button>
              </IconActionTooltip>
              <IconActionTooltip label="New chat" side="right">
                <Button
                  data-testid="new-chat-btn"
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label="New chat"
                  className="h-9 w-9 shrink-0 border-[#e6e8f2] text-[#5b63ea] hover:bg-[#5b63ea] hover:text-white hover:border-[#5b63ea]"
                  onClick={handleNewChat}
                >
                  <Plus size={18} className="text-current" />
                </Button>
              </IconActionTooltip>
            </div>
          ) : (
            <div className="p-4 border-b border-[#e6e8f2] flex items-center gap-2">
              <IconActionTooltip label="Collapse conversation history" side="bottom">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  data-testid="chat-history-collapse-btn"
                  aria-label="Collapse conversation history"
                  className="h-9 w-9 shrink-0 border-[#e6e8f2] text-[#5b63ea] hover:bg-[#5b63ea] hover:text-white hover:border-[#5b63ea]"
                  onClick={toggleHistoryCollapsed}
                >
                  <ChevronLeft size={18} className="text-current" />
                </Button>
              </IconActionTooltip>
              <Button
                data-testid="new-chat-btn"
                onClick={handleNewChat}
                className="flex-1 min-w-0 bg-[#5b63ea] hover:bg-[#4a52d9] text-white gap-2 h-9 text-sm"
              >
                <Plus size={14} /> New Chat
              </Button>
            </div>
          )}
          {historyCollapsed ? (
            <div className="flex flex-1 flex-col items-center pt-3 px-2 min-h-0">
              <Popover open={recentsPopoverOpen} onOpenChange={setRecentsPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label="Recent conversations"
                    title="Recent conversations"
                    data-testid="chat-recents-trigger"
                    className="h-9 w-9 shrink-0 border-[#e6e8f2] text-[#5b63ea] hover:bg-[#5b63ea] hover:text-white hover:border-[#5b63ea] data-[state=open]:border-[#cbd5e9] data-[state=open]:bg-[#eef0fb] data-[state=open]:text-[#5b63ea]"
                  >
                    <MessageSquare size={18} className="text-current" strokeWidth={2} />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  side="right"
                  sideOffset={10}
                  className="w-[min(320px,calc(100vw-6rem))] rounded-xl border-[#e6e8f2] bg-white p-0 shadow-xl overflow-hidden"
                >
                  <ChatRecentsPopoverPanel
                    sessionsLoading={sessionsLoading}
                    sessions={sessions}
                    activeSession={activeSession}
                    onPickSession={(sessionId) => {
                      setRecentsPopoverOpen(false);
                      loadSession(sessionId);
                    }}
                    onDeleteSession={(e, sessionId) => {
                      handleDeleteSession(e, sessionId);
                      setRecentsPopoverOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
          ) : (
            <ScrollArea className="flex-1 overflow-y-auto py-2">
              {sessionsLoading ? (
                <div className="space-y-2 px-2" aria-busy="true" aria-label="Loading conversations">
                  {Array.from({ length: 8 }).map((_, si) => (
                    <div key={`sess-sk-${si}`} className="rounded-lg px-3 py-2">
                      <div className="min-w-0 space-y-2 py-0.5">
                        <div className="h-3.5 w-[85%] max-w-[200px] animate-pulse rounded bg-[#e8eaef]" />
                        <div className="h-3 w-[45%] animate-pulse rounded bg-[#e8eaef]" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : sessions.length === 0 ? (
                <div className="px-4 py-8 text-center text-xs text-[#9ca3af]">No conversations yet</div>
              ) : (
                sessions.map((s) => {
                  const active = activeSession === s.session_id;
                  const timeLabel = formatSessionTimestamp(s.updated_at, s.created_at);
                  return (
                    <div
                      key={s.session_id}
                      data-testid={`session-${s.session_id}`}
                      role="button"
                      tabIndex={0}
                      aria-current={active ? 'true' : undefined}
                      onClick={() => loadSession(s.session_id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          loadSession(s.session_id);
                        }
                      }}
                      className={`group mx-2 mb-1 flex cursor-pointer items-center gap-2 rounded-lg border-l-[3px] px-3 py-2.5 text-left transition-colors duration-150 ${
                        active
                          ? 'border-[#5b63ea] bg-gray-200'
                          : 'border-transparent hover:bg-[#f1f5f9]'
                      } `}
                    >
                      <div className="min-w-0 flex-1">
                        <p
                          className={`truncate text-sm text-[#1e1e2e] ${active ? 'font-semibold' : 'font-medium'}`}
                        >
                          {s.title || 'Untitled'}
                        </p>
                        {timeLabel ? (
                          <p className="mt-0.5 text-[11px] leading-snug text-[#9ca3af]">{timeLabel}</p>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        aria-label="Delete conversation"
                        onClick={(e) => handleDeleteSession(e, s.session_id)}
                        className="shrink-0 rounded-md p-1 text-[#9ca3af] opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  );
                })
              )}
            </ScrollArea>
          )}
        </div>

        <div className="flex-1 flex flex-col min-w-0 bg-[#f8f9fc]">
          <div className="px-5 py-2.5 bg-white border-b border-[#e6e8f2] flex flex-wrap items-center gap-3">
            <span className="text-xs text-[#9ca3af] font-medium">Source:</span>
            <select
              data-testid="chat-source-select"
              value={selectedSource}
              onChange={(e) => setSelectedSource(e.target.value)}
              disabled={loading}
              className="text-sm text-[#1e1e2e] bg-[#f5f6fb] border border-[#e6e8f2] rounded-lg px-3 py-1.5 outline-none focus:ring-1 focus:ring-[#5b63ea]"
            >
              {sources.map((s) => (
                <option key={s.id} value={s.id}>{s.name} ({s.type})</option>
              ))}
              {sources.length === 0 && <option value="">No sources connected</option>}
            </select>
            <div className="flex-1 min-w-[8px]" />
            {activeSession && (
              <IconActionTooltip label="Export conversation as JSON" side="bottom">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label="Export conversation as JSON"
                  data-testid="chat-export-json-btn"
                  className={ASSISTANT_FOOTER_ICON_BTN}
                  onClick={handleExportConversation}
                >
                  <FileDown size={18} strokeWidth={2.25} />
                </Button>
              </IconActionTooltip>
            )}
          </div>

          <ScrollArea data-testid="chat-messages" className="flex-1 overflow-y-auto px-5 py-6 space-y-6">
            {messages.length === 0 && !loading && (
              <div data-testid="chat-empty-state" className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#5b63ea] to-[#8b5cf6] flex items-center justify-center mb-4">
                  <MessageSquare size={28} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#1e1e2e] mb-2" style={{ fontFamily: 'Outfit' }}>Ask DQP</h3>
                <p className="text-sm text-[#6b7280] mb-6 max-w-md">
                  Streaming answers with live sections - summary, reasoning, and query results as they arrive.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg w-full">
                  {suggestionsLoading && selectedSource ? (
                    Array.from({ length: 6 }).map((_, i) => (
                      <div
                        key={`sk-${i}`}
                        data-testid={`suggestion-skeleton-${i}`}
                        className="rounded-lg border border-[#e6e8f2] bg-white px-3 py-2.5 min-h-[42px] overflow-hidden"
                      >
                        <div className="h-3 rounded bg-[#e6e8f2] animate-pulse w-[92%] mb-2" />
                        <div className="h-3 rounded bg-[#eef0f6] animate-pulse w-[55%]" />
                      </div>
                    ))
                  ) : (
                    suggestions.slice(0, 6).map((q, i) => (
                      <button
                        key={i}
                        type="button"
                        data-testid={`suggestion-${i}`}
                        onClick={() => handleSend(q)}
                        disabled={!selectedSource}
                        className="text-left px-3 py-2.5 rounded-lg border border-[#e6e8f2] bg-white hover:border-[#5b63ea] hover:bg-[#5b63ea]/5 transition-all text-xs text-[#4b5563] leading-relaxed disabled:opacity-50 disabled:pointer-events-none"
                      >
                        {q}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={`${i}-${msg.timestamp || ''}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'user' ? (
                  <div data-testid={`msg-user-${i}`} className="max-w-[75%] bg-[#5b63ea] text-white px-4 py-2.5 rounded-2xl rounded-br-md text-sm leading-relaxed shadow-sm">
                    {msg.content}
                  </div>
                ) : msg.v2 || msg.sections ? (
                  <AssistantMessageV2
                    msg={msg}
                    messageIndex={i}
                    messages={messages}
                    selectedSource={selectedSource}
                    activeSession={activeSession}
                    isLatestAssistant={i === lastAssistantMessageIndex}
                    onRetry={handleRetry}
                    retryDisabled={loading || lastAssistantStreaming}
                  />
                ) : (
                  <div data-testid={`msg-assistant-${i}`} className="max-w-[85%] bg-white border border-[#e6e8f2] rounded-2xl rounded-bl-md px-5 py-4 shadow-sm">
                    <div className="text-sm text-[#1e1e2e] leading-relaxed">
                      <MarkdownBlock>{msg.content}</MarkdownBlock>
                    </div>
                    {msg.explanation?.summary && !msg.error && (
                      <div data-testid="explanation-summary" className="mt-3 rounded-lg bg-[#f8f9fc] border border-[#e6e8f2] px-3 py-2.5">
                        <p className="text-[10px] font-semibold text-[#9ca3af] uppercase tracking-wider mb-1.5">Summary</p>
                        <div className="text-sm text-[#4b5563] leading-relaxed">
                          <MarkdownBlock>{msg.explanation.summary}</MarkdownBlock>
                        </div>
                      </div>
                    )}
                    {msg.explanation && !msg.error && (
                      <div className="mt-3 flex items-center gap-3 flex-wrap">
                        {msg.explanation.confidence > 0 && <ConfidenceBadge confidence={msg.explanation.confidence} />}
                        <DataLineage lineage={msg.explanation.data_lineage} />
                      </div>
                    )}
                    <SQLDisplay sql={msg.sql_display} />
                    <QueryPlan plan={msg.query_plan} />
                    {msg.results?.columns?.length > 0 && msg.results?.rows?.length > 0 && (
                      <div className="mt-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Table2 size={14} className="text-[#5b63ea]" />
                          <span className="text-[11px] font-semibold uppercase tracking-wide text-[#9ca3af]">Results</span>
                        </div>
                        <AutoChart results={msg.results} />
                      </div>
                    )}
                    {(msg.results?.rows?.length > 0 || (i === lastAssistantMessageIndex && activeSession)) && (
                      <div className="mt-3 flex flex-wrap items-center gap-0 pt-3 border-t border-[#e6e8f2]">
                        {msg.results?.rows?.length > 0 && (
                          <IconActionTooltip label="Export results as CSV" side="top">
                            <Button
                              variant="outline"
                              size="icon"
                              type="button"
                              data-testid="export-csv-btn"
                              aria-label="Export results as CSV"
                              className={ASSISTANT_FOOTER_ICON_BTN}
                              onClick={() => {
                                const header = msg.results.columns.join(',');
                                const rows = msg.results.rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
                                const csv = [header, ...rows].join('\n');
                                const blob = new Blob([csv], { type: 'text/csv' });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `query_results_${Date.now()}.csv`;
                                a.click();
                                URL.revokeObjectURL(url);
                                toast.success('CSV exported');
                              }}
                            >
                              <Download size={18} strokeWidth={2.25} />
                            </Button>
                          </IconActionTooltip>
                        )}
                        {i === lastAssistantMessageIndex && activeSession && (
                            <IconActionTooltip label="Retry last answer (same question)" side="top">
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                aria-label="Retry answer"
                                data-testid="chat-retry-btn"
                                disabled={loading || lastAssistantStreaming}
                                className={ASSISTANT_FOOTER_ICON_BTN}
                                onClick={handleRetry}
                              >
                                <RotateCw size={18} strokeWidth={2.25} />
                              </Button>
                            </IconActionTooltip>
                        )}
                      </div>
                    )}
                    {msg.error && (
                      <p className="mt-2 text-sm text-red-600">{msg.content}</p>
                    )}
                  </div>
                )}
              </div>
            ))}

            <div ref={chatEndRef} />
          </ScrollArea>

          <div data-testid="chat-input-area" className="border-t border-[#e6e8f2] bg-white px-5 py-4">
            <div className="flex items-center gap-3 max-w-4xl mx-auto">
              <div className="flex flex-1 min-w-0 items-center gap-1 rounded-xl border border-[#e6e8f2] bg-[#f8f9fc] px-3 py-2 min-h-[44px] focus-within:ring-2 focus-within:ring-[#5b63ea]/25 focus-within:border-[#5b63ea]/35 transition-shadow">
                <textarea
                  ref={inputRef}
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={loading ? 'Waiting for response…' : 'Ask a question about your data…'}
                  rows={1}
                  disabled={loading}
                  className="flex-1 min-w-0 resize-none bg-transparent border-0 px-1 py-1.5 text-sm text-[#1e293b] placeholder:text-[#9ca3af] focus:outline-none focus:ring-0 disabled:opacity-60 leading-snug"
                  style={{ minHeight: '28px', maxHeight: '120px' }}
                  onInput={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                  }}
                />
                <button
                  type="button"
                  title="Voice input (coming soon)"
                  aria-label="Voice input (coming soon)"
                  disabled
                  className="shrink-0 flex items-center justify-center h-9 w-9 rounded-lg text-[#9ca3af] hover:text-[#5b63ea] hover:bg-[#eef0f6] transition-colors disabled:pointer-events-none disabled:opacity-45"
                >
                  <Mic size={18} strokeWidth={2} />
                </button>
              </div>
              <Button
                data-testid="chat-send-btn"
                type="button"
                onClick={() => handleSend()}
                disabled={!input.trim() || loading || !selectedSource}
                className="h-11 w-11 shrink-0 rounded-xl bg-[#5b63ea] hover:bg-[#4a52d9] text-white p-0 disabled:opacity-40"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              </Button>
            </div>
          </div>
        </div>
      </div>
      </TooltipProvider>
    </DashboardLayout>
  );
}
